import React, { useState } from 'react';
import { Button, Tooltip, Space } from 'antd';
import { EnvironmentOutlined, CopyOutlined, GlobalOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Position GPS relevee par l'application au moment de la declaration.
 *
 * La carte est chargee A LA DEMANDE, jamais au montage. Deux raisons : elle sort
 * vers openstreetmap.org, ce qu'un poste de back-office peut ne pas pouvoir faire ;
 * et un iframe muet a l'ouverture de chaque dossier ressemblerait a une panne.
 * Tant qu'on ne clique pas, les coordonnees brutes restent lisibles et copiables -
 * c'est le minimum garanti, hors ligne comme en ligne.
 *
 * Aucune dependance cartographique n'est ajoutee au bundle : un fond de carte
 * complet (leaflet & co) pour afficher un seul point ne se justifie pas.
 */

const DELTA = 0.004; // ~450 m de part et d'autre : le quartier, pas la ville

const estCoordonneeValide = (v) => typeof v === 'number' && Number.isFinite(v);

export default function CarteGps({ latitude, longitude, hauteur = 260 }) {
  const { t } = useTranslation('dossierSinistre');
  const [carteVisible, setCarteVisible] = useState(false);

  const lat = Number(latitude);
  const lon = Number(longitude);
  // 0/0 est le « null island » de l'Atlantique : c'est une position par defaut
  // d'appareil mal initialise, jamais un lieu d'accident au Togo.
  const valide = estCoordonneeValide(lat) && estCoordonneeValide(lon)
    && Math.abs(lat) <= 90 && Math.abs(lon) <= 180
    && !(lat === 0 && lon === 0);

  if (!valide) {
    return (
      <span style={{ color: '#9ca3af', fontSize: 12.5 }}>
        {t('carteGps.aucunePosition')}
      </span>
    );
  }

  const texte = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  const bbox = [lon - DELTA, lat - DELTA, lon + DELTA, lat + DELTA].join('%2C');
  const urlEmbed = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lon}`;
  const urlOsm = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texte);
      toast.success(t('carteGps.coordonneesCopiees'));
    } catch {
      // Presse-papiers refuse hors HTTPS ou sans permission : on le dit plutot que
      // de laisser croire a une copie silencieusement perdue.
      toast.warning(t('carteGps.copieImpossible', { texte }));
    }
  };

  return (
    <div>
      <Space size={8} wrap>
        <span style={{ fontFamily: 'var(--font-main)', fontSize: 13, fontWeight: 650, color: '#111827' }}>
          <EnvironmentOutlined style={{ color: '#dc2626', marginRight: 6 }} />
          {texte}
        </span>
        <Tooltip title={t('carteGps.copierCoordonnees')}>
          <Button size="small" type="text" icon={<CopyOutlined />} onClick={copier} />
        </Tooltip>
        <Button size="small" onClick={() => setCarteVisible((v) => !v)}>
          {carteVisible ? t('carteGps.masquerCarte') : t('carteGps.afficherCarte')}
        </Button>
        <Button
          size="small"
          type="link"
          icon={<GlobalOutlined />}
          href={urlOsm}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('carteGps.ouvrirOsm')}
        </Button>
      </Space>

      {carteVisible && (
        <div style={{ marginTop: 10 }}>
          <iframe
            title={t('carteGps.titreCarte')}
            src={urlEmbed}
            style={{ width: '100%', height: hauteur, border: '1px solid #e5e7eb', borderRadius: 10 }}
            loading="lazy"
          />
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            {t('carteGps.fondDeCarte')}
          </div>
        </div>
      )}
    </div>
  );
}
